import React, { useState } from 'react';
import { login } from '../../service/userService';
import SessionService from '../../service/sessionService';
import componentStyles from './login.module.css';
import globalStyles from '../globals.module.css';
import NotificationModel from '../../model/notificationModel';
import { 
    NOTIFICATIONS_SUCCESS, 
    NOTIFICATIONS_ERROR, 
    EVENT_NOTIFICATION_PUSH, 
    EVENT_UPDATE_ACTION_BAR_VIEW, 
    EVENT_UPDATE_AUTHENTICATED } from '../../utils/constants';
import eventBus from '../../service/eventBus';
import { getActiveSports } from '../../service/feedService';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_LOGIN_SUCCESS, EVENT_LOGIN_FAILED } from '../../utils/analyticsConstants';
import { refreshSubscriptionState } from '../../service/billingService';
import PageView from '../common/pageView';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionService] = useState(SessionService.getInstance());

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await login(email, password);
            const { data } = response;

            if (data.success) {
                AnalyticsService.getInstance().track(EVENT_LOGIN_SUCCESS);
                const {results} = data;
                sessionService.applyAuthResults(results);

                // Fetch and cache active sports after successful login
                try {
                    const activeSportsResponse = await getActiveSports();
                    if (activeSportsResponse?.data?.success && activeSportsResponse?.data?.results) {
                        // Store active sports in session service (cached for 1 hour)
                        sessionService.setActiveSports(activeSportsResponse.data.results);
                    }
                } catch (error) {
                    // Log error but don't block login if active sports fetch fails
                    console.error('Error fetching active sports on login:', error);
                }

                try {
                    await refreshSubscriptionState();
                } catch (error) {
                    console.error('Error fetching subscription on login:', error);
                }

                eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                    new NotificationModel('Login successful!', NOTIFICATIONS_SUCCESS)
                );
                setTimeout(() => {
                    eventBus.emit(EVENT_UPDATE_AUTHENTICATED, true);
                    eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'snipedOdds');
                }, 500);
            } else {
                AnalyticsService.getInstance().track(EVENT_LOGIN_FAILED);
                eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                    new NotificationModel('Login failed. Please check your credentials.', NOTIFICATIONS_ERROR)
                );
            }
        } catch (error) {
            AnalyticsService.getInstance().track(EVENT_LOGIN_FAILED);
            eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                new NotificationModel('An error occurred during login.', NOTIFICATIONS_ERROR)
            );
            console.error('Login error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegisterClick = () => {
        eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'register');
    };

    const handleForgotPasswordClick = () => {
        eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'reset-password');
    };

    return (
        <PageView title="Account Login" contentClassName={componentStyles.loginContainer}>
            <section className={`${globalStyles.standardSection} ${componentStyles.loginCard}`}>
                <div className={componentStyles.panelLabel}>Secure access // account terminal</div>
                <form onSubmit={handleSubmit} className={globalStyles.form}>
                    <div className={globalStyles.formGroup}>
                        <label>Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            className={globalStyles.standardInput}
                        />
                    </div>
                    <div className={globalStyles.formGroup}>
                        <label>Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            required
                            className={globalStyles.standardInput}
                        />
                        <div className={componentStyles.forgotPassword}>
                            <button 
                                type="button" 
                                onClick={handleForgotPasswordClick}
                                className={globalStyles.textButton}
                            >
                                Forgot Password?
                            </button>
                        </div>
                    </div>
                    <button 
                        type="submit" 
                        className={`${globalStyles.primaryButton} ${componentStyles.fullWidthButton}`}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
                <div className={componentStyles.registerPrompt}>
                    <p>Don't have an account?</p>
                    <button 
                        onClick={handleRegisterClick}
                        className={`${globalStyles.secondaryButton} ${componentStyles.fullWidthButton}`}
                    >
                        Register
                    </button>
                </div>
            </section>
        </PageView>
    );
};

export default Login; 
